import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommentsService } from '@home/src/app/core/services/comments/comments.service';
import { INotification } from '@home/src/app/Interface/notification';
import { Subject, takeUntil } from 'rxjs';
import { SingleNotificationComponent } from '../single-notification/single-notification.component';
import {
  NOTIFICATION_EVENT_NAME,
  MessageActions,
} from '@home/src/app/shared/constants/signalr-constants';
import { AuthenticationService } from '@home/src/app/core/services/Auth/authservice';
import { IMessage } from '@home/src/app/Interface/messages';
import { GlobalService } from '@home/src/app/core/services/global/global.service';
import { dateToMilliseconds } from '@home/src/app/enums/Date';
import { SignalrService } from '@home/src/app/core/services/signalr/signalr.service';

@Component({
  selector: 'app-notification-section',
  standalone: true,
  imports: [SingleNotificationComponent],
  templateUrl: './notification-section.component.html',
  styleUrl: './notification-section.component.css',
})
export class NotificationSectionComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  isNotificationOpen: boolean = false;
  serverNotifications: INotification[] = [];
  localNotifications: INotification[] = [];

  authUser: any = null;
  messages: IMessage[] = [];

  constructor(
    private commentsService: CommentsService,
    private authService: AuthenticationService,
    private globalService: GlobalService,
    private signalrService: SignalrService,
  ) {}

  ngOnInit(): void {
    this.authService.fincomData$
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        this.authUser = state;
      });
    this.commentsService.isNotificationOpen$
      .pipe(takeUntil(this.destroy$))
      .subscribe((open) => {
        this.isNotificationOpen = open;
      });
    this.commentsService.serverNotifications$
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.serverNotifications = value;
      });
    this.commentsService.localNotifications$
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.localNotifications = value;
      });
    this.signalrService.connectionState$
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        if (state === 'connected') {
          this.signalrService.on(NOTIFICATION_EVENT_NAME, (data) =>
            this.newNotification(data),
          );
        }
      });
  }

  get allNotifications() {
    let allNotifications = [
      ...this.serverNotifications,
      ...this.localNotifications,
    ];
    if (!allNotifications.length) return [];
    allNotifications = allNotifications.map((notification) => {
      if (notification.isServer) {
        return {
          ...notification,
          millseconds: dateToMilliseconds(notification.createdDate),
        };
      } else {
        return {
          ...notification,
          millseconds: Date.parse(notification.createdDate),
        };
      }
    });
    return allNotifications.sort((a, b) => {
      if (b.millseconds && a.millseconds)
        return b?.millseconds - a?.millseconds;
      else return 0;
    });
  }

  async newNotification(data: any) {
    const actionValue = data?.action || data?.Action;
    if (!data || !actionValue) return;
    if (actionValue === MessageActions.inserted) {
      await this.commentsService.getAllNotifications(this.authUser?.email);
      if (data?.users?.some((item: any) => item === this.authUser?.email)) {
        this.globalService.showToast(`New Notification recieved`, 'success');
      }
    } else if (actionValue === MessageActions.deleted) {
      await this.commentsService.getAllNotifications(this.authUser?.email);
    } else if (actionValue === MessageActions.marked_as_read) {
      await this.commentsService.getAllNotifications(this.authUser?.email);
    } else if (actionValue === MessageActions.updated) {
      await this.commentsService.getAllNotifications(this.authUser?.email);
      if (data?.users?.some((item: any) => item === this.authUser?.email)) {
        this.globalService.showToast(data?.message, 'success');
      }
    }
  }

  closePanel() {
    const unreadMessageIds = this.serverNotifications
      .filter((msg) => !msg.isRead)
      .map((msg) => msg.notificationId);
    if (unreadMessageIds.length)
      this.commentsService.markServerNotificationsAsRead(
        unreadMessageIds,
        this.authUser?.email,
      );

    const localNotifications = this.localNotifications.map(
      (notification: INotification) => {
        return { ...notification, isRead: true };
      },
    );
    this.commentsService.setAllLocalNotifications(localNotifications);
    this.commentsService.setNotificationOpen(false);
  }

  async clearAll() {
    const payload = {
      emailAddress: this.authUser?.email,
      notificationIds: this.serverNotifications.map(
        (notification) => notification.notificationId,
      ),
    };
    const response =
      await this.commentsService.clearAllServerNotifications(payload);
    if (response?.statusCode === 200) {
      this.commentsService.clearAllLocalNotifications();
      this.globalService.showToast(
        'All notifications cleared successfully',
        'success',
      );
    } else {
      this.globalService.showToast(
        'Failed to clear notifications. Please try again.',
        'error',
      );
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
